import { useState } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Link } from 'react-router-dom';
import { Search, Calendar, User, ArrowRight, BookOpen, Filter, Phone } from 'lucide-react';

const Articoli = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Articoli statici di esempio
  const articoli = [
    {
      id: 1,
      titolo: "Prevenzione del Mal di Schiena",
      slug: "prevenzione-mal-di-schiena",
      riassunto: "Consigli pratici per prevenire il mal di schiena nella vita quotidiana e mantenere una postura corretta",
      data_pubblicazione: "2024-12-01",
      tempo_lettura: 5,
      immagine_copertina: "/images/fisioterapia_centro_1.jpeg",
      categoria: { nome: "Prevenzione", colore: "blue" }
    },
    {
      id: 2,
      titolo: "Esercizi Posturali per Bambini",
      slug: "esercizi-posturali-bambini",
      riassunto: "Guida completa agli esercizi posturali specifici per l'età evolutiva e la crescita sana",
      data_pubblicazione: "2024-11-28",
      tempo_lettura: 7,
      immagine_copertina: "/images/terapia_neonatale_1.jpeg",
      categoria: { nome: "Pediatria", colore: "green" }
    },
    {
      id: 3,
      titolo: "Riabilitazione Post-Chirurgica",
      slug: "riabilitazione-post-chirurgica",
      riassunto: "Protocolli e tempistiche per una riabilitazione efficace dopo interventi chirurgici ortopedici",
      data_pubblicazione: "2024-11-25",
      tempo_lettura: 8,
      immagine_copertina: "/images/fisioterapia_centro_2.webp",
      categoria: { nome: "Riabilitazione", colore: "purple" }
    },
    {
      id: 4,
      titolo: "Plagiocefalie: Prevenzione e Trattamento",
      slug: "plagiocefalie-prevenzione-trattamento",
      riassunto: "Tutto quello che i genitori devono sapere sulle deformità craniche posizionali nei neonati",
      data_pubblicazione: "2024-11-20",
      tempo_lettura: 6,
      immagine_copertina: "/images/plagiocefalia_trattamento_1.jpeg",
      categoria: { nome: "Neonatale", colore: "pink" }
    },
    {
      id: 5,
      titolo: "Torcicollo Miogeno: Riconoscimento Precoce",
      slug: "torcicollo-miogeno-riconoscimento",
      riassunto: "Come riconoscere i segni del torcicollo miogeno congenito e l'importanza dell'intervento precoce",
      data_pubblicazione: "2024-11-15",
      tempo_lettura: 5,
      immagine_copertina: "/images/terapia_neonatale_2.jpeg",
      categoria: { nome: "Neonatale", colore: "pink" }
    },
    {
      id: 6,
      titolo: "Recupero Sportivo: Metodologie Avanzate",
      slug: "recupero-sportivo-metodologie",
      riassunto: "Tecniche moderne per il recupero degli atleti e la prevenzione degli infortuni sportivi",
      data_pubblicazione: "2024-11-10",
      tempo_lettura: 9,
      immagine_copertina: "/images/fitness_trainer_1.jpeg",
      categoria: { nome: "Sport", colore: "orange" }
    }
  ];

  const categorie = [
    { nome: "Prevenzione", colore: "blue" },
    { nome: "Pediatria", colore: "green" },
    { nome: "Riabilitazione", colore: "purple" },
    { nome: "Neonatale", colore: "pink" },
    { nome: "Sport", colore: "orange" }
  ];

  const filteredArticoli = articoli.filter(articolo => {
    const matchesSearch = articolo.titolo.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         articolo.riassunto.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || 
                           articolo.categoria.nome === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5">
        <div className="container-custom">
          <div className="text-center space-y-4 mb-12">
            <Badge className="bg-primary/10 text-primary border-primary/20">
              <BookOpen className="mr-2 h-4 w-4" />
              Blog
            </Badge>
            <h1 className="text-4xl lg:text-5xl font-bold">Articoli e Consigli</h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Consigli professionali, guide pratiche e approfondimenti sulla riabilitazione 
              motoria e il benessere fisico
            </p>
          </div>

          {/* Filtri */}
          <div className="max-w-2xl mx-auto">
            <Card className="medical-card">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Cerca articoli..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div className="md:w-48">
                    <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                      <SelectTrigger>
                        <Filter className="mr-2 h-4 w-4" />
                        <SelectValue placeholder="Categoria" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tutte le categorie</SelectItem>
                        {categorie.map((categoria, index) => (
                          <SelectItem key={index} value={categoria.nome}>
                            {categoria.nome}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Lista Articoli */}
      <section className="section-padding">
        <div className="container-custom">
          {filteredArticoli.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Nessun articolo trovato per i criteri selezionati.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredArticoli.map((articolo) => (
                <Card key={articolo.id} className="medical-card group hover:scale-105 smooth-transition overflow-hidden">
                  <div className="relative overflow-hidden">
                    <img 
                      src={articolo.immagine_copertina} 
                      alt={articolo.titolo}
                      className="w-full h-48 object-cover group-hover:scale-110 smooth-transition"
                    />
                    <div className="absolute top-4 left-4">
                      <Badge className="bg-primary/90 text-white border-white/30">
                        {articolo.categoria.nome}
                      </Badge>
                    </div>
                  </div>
                  
                  <CardHeader>
                    <CardTitle className="text-lg line-clamp-2 group-hover:text-primary smooth-transition">
                      {articolo.titolo}
                    </CardTitle>
                    <CardDescription className="line-clamp-3">
                      {articolo.riassunto}
                    </CardDescription>
                  </CardHeader>
                  
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                      <div className="flex items-center space-x-2">
                        <Calendar className="h-4 w-4" />
                        <span>{formatDate(articolo.data_pubblicazione)}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <User className="h-4 w-4" />
                        <span>{articolo.tempo_lettura} min</span>
                      </div>
                    </div>
                    
                    <Button variant="outline" className="w-full group-hover:bg-primary group-hover:text-white smooth-transition" asChild>
                      <Link to={`/articoli/${articolo.slug}`}>
                        Leggi Articolo
                        <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 smooth-transition" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Articoli in Evidenza */}
      <section className="section-padding bg-muted/30">
        <div className="container-custom">
          <div className="text-center space-y-4 mb-12">
            <Badge className="bg-secondary/10 text-secondary border-secondary/20">
              In Evidenza
            </Badge>
            <h2 className="text-4xl font-bold">Articoli Più Letti</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card className="medical-card">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <img 
                    src="/images/plagiocefalia_trattamento_1.jpeg" 
                    alt="Plagiocefalie"
                    className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                  />
                  <div className="flex-1">
                    <h3 className="font-semibold mb-2">Plagiocefalie: Guida per Genitori</h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      Tutto quello che devi sapere sulle deformità craniche posizionali
                    </p>
                    <Badge variant="secondary" className="text-xs">Più Letto</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="medical-card">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <img 
                    src="/images/terapia_neonatale_1.jpeg" 
                    alt="Esercizi Bambini"
                    className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                  />
                  <div className="flex-1">
                    <h3 className="font-semibold mb-2">Esercizi Posturali per Bambini</h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      Guida pratica per genitori e educatori
                    </p>
                    <Badge variant="secondary" className="text-xs">Consigliato</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding medical-gradient text-white">
        <div className="container-custom text-center space-y-8">
          <h2 className="text-4xl font-bold">Hai Domande Specifiche?</h2>
          <p className="text-xl opacity-90 max-w-2xl mx-auto">
            Contattaci per approfondimenti personalizzati o per prenotare una consulenza
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" variant="secondary" className="bg-white text-primary hover:bg-white/90" asChild>
              <a href="https://www.sumupbookings.com/dott-papa-peppino-salvatore" target="_blank" rel="noopener noreferrer">
                <Calendar className="mr-2 h-4 w-4" />
                Prenota Consulenza
              </a>
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10" asChild>
              <a href="tel:+393475391433">
                <Phone className="mr-2 h-4 w-4" />
                Chiama: +39 347 5391433
              </a>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Articoli;